import { updateUser } from '@api/profile';
import useToastContext from '@contexts/ToastContext/hook';
import { PlatformUserAddress } from '@interfaces/platformUserAddress/platformUserAddress';
import { PlatformUser } from '@interfaces/profile';
import { Button } from '@vibTheme/components/_base/Button';
import { Image } from '@vibTheme/components/_base/Image';
import { Typography } from '@vibTheme/components/_base/Typography';
import { VibModal } from '@vibTheme/components/VibModal';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

const SHOW_DIALOG_INTERVAL = 31556952000; // 1 ano

type Props = {
  platformUser?: PlatformUser;
  platformUserAddress?: PlatformUserAddress;
};

export const ReviewProfileDialog = (props: Props) => {
  const { platformUser, platformUserAddress } = props;
  if (!platformUser) return null;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const updateLastProfileAlertDate = async () => {
    await updateUser({ lastProfileUpdateAlertDate: new Date().toISOString() });
  };

  const toast = useToastContext();
  const handleDismiss = async () => {
    await updateLastProfileAlertDate();
    toast.setContent('Obrigado! Seus dados foram confirmados 😊');
    setIsModalOpen(false);
  };

  const router = useRouter();
  const handleUpdateData = async () => {
    await updateLastProfileAlertDate();
    router.push(`/profile/personalData`);
    setIsModalOpen(false);
  };

  useEffect(() => {
    const date = new Date(
      platformUser.lastProfileUpdateAlertDate || platformUser.creationDate,
    );
    const shouldShowAlert = Date.now() - date.getTime() > SHOW_DIALOG_INTERVAL;
    shouldShowAlert && setIsModalOpen(true);
  }, [platformUser]);

  if (!isModalOpen) return null;

  return (
    <VibModal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
      <div className="max-w-[1000px] md:p-main">
        <div className="flex flex-col gap-main md:flex-row md:gap-[5rem]">
          <div className="relative h-[193px] md:w-[357px] md:h-[438px]">
            <Image
              src="/assets/girl-on-call.svg"
              alt="Garota no telefone"
              fill
            />
          </div>
          <div className="md:w-[530px]">
            <Typography component="h1" className="pb-main">
              Mantenha seus dados atualizados
            </Typography>
            <Typography>
              Para garantir o envio correto de notas e comunicações, revise e
              confirme suas informações.
            </Typography>

            <div className="pt-main grid md:grid-cols-2 gap-main">
              <div>
                <Typography className="text-sm">E-mail cadastrado:</Typography>
                <Typography className="text-sm font-semibold">
                  {platformUser.email || 'Não informado'}
                </Typography>
              </div>
              <div>
                <Typography className="text-sm">Nome social:</Typography>
                <Typography className="text-sm font-semibold">
                  {platformUser.preferredName || 'Não informado'}
                </Typography>
              </div>
              <div>
                <Typography className="text-sm">Celular cadastrado:</Typography>
                <Typography className="text-sm font-semibold">
                  {platformUser.phone || 'Não informado'}
                </Typography>
              </div>
              <div>
                <Typography className="text-sm">Como se identifica:</Typography>
                <Typography className="text-sm font-semibold">
                  {platformUser.gender || 'Não informado'}
                </Typography>
              </div>
              <div className="md:col-span-2">
                <Typography className="text-sm">Endereço:</Typography>
                <Typography className="text-sm font-semibold">
                  {!platformUserAddress?.id && 'Não informado'}
                  {platformUserAddress?.id &&
                    renderFormattedAddress(platformUserAddress)}
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-end gap-main pt-main">
          <Button onClick={handleUpdateData}>Atualizar dados</Button>
          <Button variant="text" onClick={handleDismiss}>
            Estão corretos
          </Button>
        </div>
      </div>
    </VibModal>
  );
};

const renderFormattedAddress = (address: PlatformUserAddress) =>
  `${address.postalCode} ${address.street}, ${address.number} ${
    address.complement || ''
  } - ${address.neighborhood}. ${address.city} - ${address.stateCode}`;
